import { useCallback, useEffect, useState } from 'react'

import { useMutation } from '@apollo/client'
import { Avatar, TextInput, Tooltip } from '@mantine/core'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'

import { useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DnTable from 'src/components/Core/DnTable'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  DELETE_TASK_TEMPLATE,
  GET_TASK_TEMPLATES,
} from 'src/graphql/taskTemplates.graphql'
import IconSearch from 'src/icons/IconSearch'
import { buildFullName } from 'src/lib/formatters'

import ActionsCell from './components/ActionsCell'
import IdCell from './components/IdCell'
import TaskTemplateModal from './components/TaskTemplateModal'

const onSuccess = () => {
  toast('Your task template was deleted successfully', 'success')
}

const TaskTemplatesTab = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [editing, setEditing] = useState(false)
  const [createModalOpened, createModalHandlers] = useDisclosure(false)
  const [deleteModalOpened, deleteModalHandlers] = useDisclosure(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 300)
  const LIMIT = 20

  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const {
    data: taskTemplatesList,
    loading: taskTemplatesListLoading,
    refetch,
  } = useQuery(GET_TASK_TEMPLATES, {
    variables: {
      pageInfo: {
        limit: LIMIT,
        offset: 0,
      },
      orderBy: {
        field: 'title',
        direction: 'asc',
      },
      search: debouncedSearchTerm || undefined,
    },
    onError,
  })

  const handleLoadPage = useCallback(
    ({ offset, limit }: { offset: number; limit: number }) => {
      refetch({
        pageInfo: {
          limit,
          offset,
        },
        orderBy: {
          field: 'title',
          direction: 'asc',
        },
        search: debouncedSearchTerm || undefined,
      })
    },
    [refetch, debouncedSearchTerm]
  )

  //MUTATIONS:
  const [deleteTaskTemplate] = useMutation(DELETE_TASK_TEMPLATE, {
    refetchQueries: [GET_TASK_TEMPLATES],
    onCompleted: () => {
      onSuccess()
      // Reload current page
      handleLoadPage({ offset: 0, limit: LIMIT })
    },
    onError,
  })

  //HANDLERS:
  const onEdit = (element) => {
    setSelectedTemplate(element)
    setEditing(true)
    createModalHandlers.open()
  }

  const onCreate = () => {
    setSelectedTemplate(undefined)
    setEditing(false)
    createModalHandlers.open()
  }

  const onDelete = () => {
    deleteTaskTemplate({
      variables: {
        taskTemplateId: selectedTemplate.taskTemplateId,
      },
    })
    deleteModalHandlers.close()
  }

  const handleDelete = (template) => {
    setSelectedTemplate(template)
    deleteModalHandlers.open()
  }

  // This resets the selected template to null when the modal is closed
  useEffect(() => {
    if (!createModalOpened) {
      setSelectedTemplate(null)
    }
  }, [createModalOpened])

  const templates = taskTemplatesList?.taskTemplates?.items ?? []
  const totalCount = taskTemplatesList?.taskTemplates?.totalCount || 0

  // Column definitions for DnTable
  const columns = [
    {
      key: 'taskTemplateId',
      label: 'ID',
      width: 8,
      render: (template) => (
        <IdCell
          key={`id-${template.taskTemplateId}`}
          taskTemplateId={template.taskTemplateId}
        />
      ),
    },
    {
      key: 'title',
      label: 'Task Title',
      width: 25,
      render: (template) => template.title,
    },
    {
      key: 'createdBy',
      label: 'Created By',
      width: 10,
      render: (template) =>
        template.createdBy ? (
          <Tooltip label={buildFullName(template.createdBy)} withArrow>
            <Avatar color="blue" size="sm" radius="sm">
              {template.createdBy.firstName?.charAt(0)}
              {template.createdBy.lastName?.charAt(0)}
            </Avatar>
          </Tooltip>
        ) : (
          'N/A'
        ),
    },
    {
      key: 'createdAt',
      label: 'Created Date',
      render: (template) => (
        <Tooltip
          label={dayjs(template.createdAt).format('MM-DD-YYYY h:mm A')}
          withArrow
        >
          <span>{dayjs(template.createdAt).format('MM-DD-YYYY')}</span>
        </Tooltip>
      ),
    },
    {
      key: 'updatedAt',
      label: 'Updated Date',
      render: (template) => (
        <Tooltip
          label={dayjs(template.updatedAt).format('MM-DD-YYYY h:mm A')}
          withArrow
        >
          <span>{dayjs(template.updatedAt).format('MM-DD-YYYY')}</span>
        </Tooltip>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: 10,
      render: (template) => (
        <ActionsCell
          key={`actions-${template.taskTemplateId}`}
          template={template}
          onEdit={onEdit}
          onDelete={handleDelete}
        />
      ),
    },
  ]

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex flex-row justify-between">
        <div className="flex flex-col ">
          <div className="text-lg font-semibold text-doubleNickel-gray-900">
            Task Templates
          </div>
          <div className="text-sm text-doubleNickel-gray-600">
            {taskTemplatesListLoading
              ? 'loading...'
              : `${totalCount} task templates`}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <TextInput
            placeholder="Search templates..."
            leftSection={
              <IconSearch className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
            className="w-64"
          />
          <DNButton variant="primary" onClick={() => onCreate()}>
            Create Template
          </DNButton>
        </div>
      </div>
      <div className="min-h-0 flex-1">
        <DnTable
          columns={columns}
          data={templates}
          enableColumnResize={false}
          totalCount={totalCount}
          onLoadPage={handleLoadPage}
          loading={taskTemplatesListLoading}
          onRowClick={(idx) => onEdit(templates[idx])}
          emptyStateMessage="No templates found. Add one to get started."
        />
      </div>
      <TaskTemplateModal
        taskTemplate={selectedTemplate}
        edit={editing}
        isOpen={createModalOpened}
        onClose={() => createModalHandlers.close()}
        refetchQueries={[GET_TASK_TEMPLATES]}
      />
      <DeleteModal
        title={`Are you sure you want to delete the template ${selectedTemplate?.title}?`}
        opened={deleteModalOpened}
        close={deleteModalHandlers.close}
        onDelete={onDelete}
      />
    </div>
  )
}

export default TaskTemplatesTab
